
import { z } from "zod";

// Schema for legacy launches
export const formSchemaLegacy = z.object({
  form_id: z.number().min(1, "Debe seleccionar un formulario"),
  id_cliente: z.number().min(1, "Debe seleccionar un cliente"),
  id_reporte: z.number().min(1, "Debe seleccionar una plantilla"),
  max_respuestas: z.number().min(0),
  report_lifetime: z.number().min(0),
  expiration_date: z.date().optional(),
  generacion_reporte: z.string().optional(),
  report_type: z.string().optional(),
  email_sending_type: z.string().optional(),
  templates: z.array(z.object({
    template_id: z.number(),
    is_primary: z.boolean(),
    priority: z.number()
  })).optional(),
});

// Schema for V2 launches
export const formSchemaV2 = z.object({
  form_mapping_id: z.number().min(1, "Debe seleccionar un formulario"),
  id_cliente: z.number().min(1, "Debe seleccionar un cliente"),
  template_v2_id: z.number().min(1, "Debe seleccionar una plantilla"),
  max_respuestas: z.number().min(0),
  report_lifetime: z.number().min(0),
  expiration_date: z.date().optional(),
  generacion_reporte: z.string().optional(),
  report_type: z.string().optional(),
  email_sending_type: z.string().optional(),
});

// Flexible schema for EditLaunchDialog that accepts both legacy and V2 fields
export const formSchemaEdit = z.object({
  // Either legacy form_id or V2 form_mapping_id
  form_id: z.number().optional(),
  form_mapping_id: z.number().optional(),

  id_cliente: z.number().min(1, "Debe seleccionar un cliente"),

  // Either legacy id_reporte or V2 template_v2_id
  id_reporte: z.number().optional(),
  template_v2_id: z.number().optional(),

  max_respuestas: z.number().min(0),
  report_lifetime: z.number().min(0),
  expiration_date: z.date().optional(),
  generacion_reporte: z.string().optional(),
  report_type: z.string().optional(),
  email_sending_type: z.string().optional(),

  // Auto-generation configuration
  auto_generate_report: z.boolean().optional(),
  template_referral_id: z.number().optional(),
  notify_on_auto_generate: z.boolean().optional(),
}).refine(
  (data) => (data.form_id && data.form_id > 0) || (data.form_mapping_id && data.form_mapping_id > 0),
  { message: "Debe seleccionar un formulario", path: ["form_mapping_id"] }
).refine(
  (data) => (data.id_reporte && data.id_reporte > 0) || (data.template_v2_id && data.template_v2_id > 0),
  { message: "Debe seleccionar una plantilla", path: ["template_v2_id"] }
);

// Union schema (will be used by CreateLaunchDialog to support V2 only)
export const formSchema = formSchemaV2;

export type FormValues = z.infer<typeof formSchema>;
export type FormValuesLegacy = z.infer<typeof formSchemaLegacy>;
export type FormValuesEdit = z.infer<typeof formSchemaEdit>;

export interface LaunchTemplate {
  id: number;
  launch_id: number;
  template_id: number;
  is_primary: boolean;
  priority: number;
  created_at: string;
  report_templates?: {
    id: number;
    name: string;
  };
}

// Complete FormLaunch interface supporting both legacy and V2
export interface FormLaunch {
  id: number;
  project_id: number;
  id_cliente: number;

  // Legacy fields (nullable for V2 launches)
  form_id?: number | null;
  id_reporte?: number | null;

  // V2 fields (nullable for legacy launches)
  form_mapping_id?: number | null;
  template_v2_id?: number | null;

  // Shared fields
  max_respuestas?: number | null;
  report_lifetime?: number | null;
  expiration_date?: string | null;
  created_at: string;
  generacion_reporte?: string | null;
  report_type?: string | null;
  email_sending_type?: string | null;
  envio_email?: string | null;

  // Auto-generation configuration
  auto_generate_report?: boolean | null;
  template_referral_id?: number | null;
  notify_on_auto_generate?: boolean | null;

  // Relations
  clientes?: {
    id: number;
    nombre: string;
    logo?: string | null;
    mision?: string | null;
  } | null;

  report_templates?: {
    id: number;
    name: string;
  } | null;

  jotform_form_mappings?: {
    id: number;
    jotform_form_id: string;
    form_title?: string | null;
    is_referral_form?: boolean | null;
  } | null;

  report_templates_v2?: {
    id: number;
    name: string;
  } | null;
}

// Type guard to check if launch is V2
export function isV2Launch(launch: FormLaunch): boolean {
  return launch.template_v2_id !== null && launch.template_v2_id !== undefined;
}

// Helper to get form data from launch
export function getLaunchFormData(launch: FormLaunch): {
  id: number;
  nombre: string;
  jotform_form_id?: string;
} | null {
  if (launch.jotform_form_mappings) {
    return {
      id: launch.jotform_form_mappings.id,
      nombre: launch.jotform_form_mappings.form_title || launch.jotform_form_mappings.jotform_form_id,
      jotform_form_id: launch.jotform_form_mappings.jotform_form_id,
    };
  }

  return null;
}

// Helper to get template data from launch (legacy or V2)
export function getLaunchTemplateData(launch: FormLaunch): {
  id: number;
  name: string;
} | null {
  if (isV2Launch(launch) && launch.report_templates_v2) {
    return launch.report_templates_v2;
  }

  if (launch.report_templates) {
    return launch.report_templates;
  }

  return null;
}
